import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, ShoppingBag, X } from "lucide-react";
import Logo from "./Logo";
import { useCart } from "@/lib/cart";

const LINKS = [
  { label: "The problem", to: "/struggling", id: "nav-link-problem" },
  { label: "Results", to: "/results", id: "nav-link-results" },
  { label: "How to use", to: "/routine", id: "nav-link-routine" },
  { label: "FAQs", to: "/faqs", id: "nav-link-faqs" },
  { label: "Shop", to: "/shop", id: "nav-link-shop" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="bg-[#E4F0E7] border-b border-[#D1E2D6]/60">
        <div className="mx-auto max-w-[88rem] px-5 sm:px-10 py-2 flex justify-center items-center gap-8 text-[0.8rem] text-[#1B4332]">
          <span data-testid="announcement-bar">A fresh start, one morning at a time.</span>
          <span className="hidden sm:inline">Meet your daily Vibe.</span>
        </div>
      </div>

      <div className="bg-[#E8F3EB]/85 backdrop-blur-xl border-b border-[#D1E2D6]/70">
        <div className="mx-auto max-w-[88rem] px-5 sm:px-10 h-[4.5rem] flex items-center justify-between">
          <Logo />

          <nav className="hidden lg:flex items-center gap-10">
            {LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={link.id}
                className={({ isActive }) =>
                  `link-underline text-[0.95rem] transition-colors duration-300 ${
                    isActive ? "is-active text-[#1B4332] font-semibold" : "text-[#4A6B56] hover:text-[#1B4332]"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="header-cart-button"
              onClick={() => setOpen(true)}
              className="relative grid place-items-center h-11 w-11 rounded-full border border-[#D1E2D6] bg-white/50 text-[#1B4332] hover:bg-[#1B4332] hover:text-[#E8F3EB] transition-colors duration-300"
              aria-label="Open cart"
            >
              <ShoppingBag size={18} strokeWidth={1.75} />
              {count > 0 && (
                <span
                  data-testid="header-cart-count"
                  className="absolute -top-1 -right-1 grid place-items-center h-5 min-w-5 px-1 rounded-full bg-[#1B4332] text-[#E8F3EB] text-[0.65rem] font-bold"
                >
                  {count}
                </span>
              )}
            </button>

            <Link
              to="/shop"
              data-testid="header-shop-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-[#E8F3EB] pl-6 pr-5 h-11 text-[0.95rem] font-semibold hover:bg-[#2D5A44] transition-colors duration-300"
            >
              Order now
              <ArrowUpRight size={16} />
            </Link>

            <button
              data-testid="header-mobile-menu-button"
              className="lg:hidden grid place-items-center h-11 w-11 rounded-full border border-[#D1E2D6] text-[#1B4332]"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden mx-5 mt-2 rounded-2xl bg-[#E8F3EB]/95 backdrop-blur-xl border border-[#D1E2D6] p-6 flex flex-col gap-5"
          >
            {LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`${link.id}-mobile`}
                onClick={() => setMobileOpen(false)}
                className="text-lg font-semibold text-[#1B4332]"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/shop"
              data-testid="nav-link-shop-mobile"
              onClick={() => setMobileOpen(false)}
              className="mt-2 inline-flex justify-center items-center gap-2 rounded-full bg-[#1B4332] text-[#E8F3EB] px-6 h-12 font-medium"
            >
              Shop now
              <ArrowUpRight size={16} />
            </Link>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
